import { useState, useEffect, useCallback } from 'react';
import config from '../config';

export const parseDateOnlyValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDateForStorage = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function for authenticated fetches
const authFetch = async (url, options = {}) => {
  const token = sessionStorage.getItem('token');

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return fetch(url, {
    ...options,
    headers,
  });
};

// Utility functions moved from App.js
export const cleanData = (obj, parentKey = '') => {
  if (obj === null || obj === undefined) return undefined;
  if (Array.isArray(obj)) {
    const cleaned = obj.map(item => cleanData(item, parentKey)).filter(item => item !== undefined);
    if (parentKey === 'credentialing' && cleaned.length === 0) {
      return [];
    }
    if (parentKey === 'courses' && cleaned.length === 0) {
      return [];
    }
    return cleaned.length > 0 ? cleaned : undefined;
  }
  if (typeof obj === 'object') {
    const cleaned = {};
    for (const key in obj) {
      if (key === 'coursesMin') {
        cleaned[key] = obj[key].map(monthObj => {
          const filteredMonthObj = {};
          for (const course in monthObj) {
            if (monthObj[course] !== '' && monthObj[course] !== null && monthObj[course] !== undefined) {
              filteredMonthObj[course] = monthObj[course];
            }
          }
          return filteredMonthObj;
        });
        continue;
      }
      // Skip 'priority' field in practices
      if (parentKey === 'practices' && key === 'priority') {
        continue;
      }
      const cleanedValue = cleanData(obj[key], key);
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue;
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }
  return obj;
};

export const extractCoursesMin = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(extractCoursesMin);
  }
  const newObj = {};
  for (const key in obj) {
    if (key === 'coursesMin') {
      newObj[key] = obj[key].map(monthObj => {
        const filteredMonthObj = {};
        for (const course in monthObj) {
          if (monthObj[course] !== '' && monthObj[course] !== null && monthObj[course] !== undefined) {
            filteredMonthObj[course] = monthObj[course];
          }
        }
        return filteredMonthObj;
      });
    } else {
      newObj[key] = extractCoursesMin(obj[key]);
    }
  }
  return newObj;
};

export const validateData = (data) => {
  const errors = [];

  if (!data.startDate || data.startDate.trim() === '') {
    errors.push('Start Date is mandatory.');
  }
  if (!data.endDate || data.endDate.trim() === '') {
    errors.push('End Date is mandatory.');
  }
  if (!data.maxPatientsPerAppt || data.maxPatientsPerAppt === 0) {
    errors.push('Maximum Patients per Appointment is mandatory.');
  }
  if (data.absenteeRate === undefined || data.absenteeRate === null || data.absenteeRate === '') {
    errors.push('Absentee Rate is mandatory.');
  }

  if (data.practices && data.practices.length > 0) {
    data.practices.forEach((practice, index) => {
      if (!practice.name || practice.name.trim() === '') {
        errors.push(`Practice ${index + 1}: Name is mandatory.`);
      }
      if (!practice.state || practice.state.trim() === '') {
        errors.push(`Practice ${index + 1}: State is mandatory.`);
      }
      if (!practice.timezone || practice.timezone.trim() === '') {
        errors.push(`Practice ${index + 1}: Timezone is mandatory.`);
      }
      if (!practice.totalPatients || practice.totalPatients === 0) {
        errors.push(`Practice ${index + 1}: Total Patients is mandatory.`);
      }
      if (!practice.workingHours || practice.workingHours.trim() === '') {
        errors.push(`Practice ${index + 1}: Working Hours is mandatory.`);
      }
    });
  }

  if (data.providers && data.providers.length > 0) {
    data.providers.forEach((providerWrapper, index) => {
      const provider = providerWrapper.provider || {};
      if (!provider.name || provider.name.trim() === '') {
        errors.push(`Provider ${index + 1}: Name is mandatory.`);
      }
      if (!provider.timezone || provider.timezone.trim() === '') {
        errors.push(`Provider ${index + 1}: Timezone is mandatory.`);
      }
      if (!provider.license || provider.license.length === 0) {
        errors.push(`Provider ${index + 1}: License is mandatory.`);
      }
    });
  }

  if (errors.length > 0) {
    return { valid: false, message: 'Please fill the mandatory fields:\n' + errors.join('\n') };
  }
  return { valid: true };
};

export const getMonthsFromDates = (startDate, endDate) => {
  const months = [];
  const start = parseDateOnlyValue(startDate);
  const end = parseDateOnlyValue(endDate);

  if (!start || !end || start > end) return [];

  let current = new Date(start.getFullYear(), start.getMonth(), 1);

  while (current <= end) {
    const monthStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
    months.push(monthStr);
    current.setMonth(current.getMonth() + 1);
  }

  return months;
};

// Hook for fetching data
export const useFetchData = (isAuthenticated = true) => {
  const [jsonData, setJsonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  const applyDefaultValues = useCallback((data) => {
    if (!data) return data;
    const defaultData = { ...data };
    defaultData.maxPatientsPerAppt = 100;
    defaultData.absenteeRate = 0.4;
    defaultData.USE_STATIC_COURSES = "false";
    defaultData.startDate = '';
    defaultData.endDate = '';
    defaultData.practices = [];
    defaultData.providers = [];
    return defaultData;
  }, []);

  const fetchData = useCallback(async () => {
    setError(null);
    setLoading(true);
    // 1. Get the token stored by App B's landing logic
    const token = sessionStorage.getItem('token');
    
    if (!token) {
      setError("No authentication token found. Please login via the portal.");
      setLoading(false);
      return;
    }

    try {
      // 2. Pass the Bearer token to trigger verify_token in auth.py via authFetch
      const response = await authFetch(`${config.SMA_SCHEDULER_SERVICE_BASE_URL}/config-current-get`);

      // The backend verifies the token on every call, so a 401 here means the token is invalid
      if (response.status === 401) {
        sessionStorage.removeItem('token');
        setIsUnauthorized(true);
        return;
      }

      if (!response.ok) {
        console.log("no use rul")
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Fetched config data:", data);
      const dataWithDefaults = applyDefaultValues(data);
      setJsonData(dataWithDefaults);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [applyDefaultValues]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Always fetch fresh data from API on mount instead of relying on localStorage
    fetchData();
  }, [fetchData, isAuthenticated]);

  return { jsonData, setJsonData, loading, error, isUnauthorized, fetchData };
};

// Hook for fetching files
export const useFetchFiles = (isAuthenticated) => {
  const [outputFiles, setOutputFiles] = useState([]);
  const [backupFiles, setBackupFiles] = useState([]);

  const fetchOutputFiles = async () => {
    try {
      const response = await authFetch(`${config.SMA_SCHEDULER_SERVICE_BASE_URL}/schedule-files-get`);
      if (response.ok) {
        const data = await response.json();
        setOutputFiles(data.files);
      }
    } catch (err) {
      console.error('Error fetching Schedule files:', err);
    }
  };

  const fetchBackupFiles = async () => {
    try {
      const response = await authFetch(`${config.SMA_SCHEDULER_SERVICE_BASE_URL}/config-all-get`);
      if (response.ok) {
        const data = await response.json();
        setBackupFiles(data.files || []);
      }
    } catch (err) {
      console.error('Error fetching backup files:', err);
      setBackupFiles([]);
    }
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (isAuthenticated && token) {
      fetchOutputFiles();
      fetchBackupFiles();
    }
  }, [isAuthenticated]);

  return { outputFiles, setOutputFiles, backupFiles, setBackupFiles, fetchOutputFiles, fetchBackupFiles };
};

// Function to handle save
export const handleSave = async (jsonData, changedFields, setNotification, fetchBackupFiles, setChangedFields, setCurrentBackupVersion, options = {}) => {
  const { notifyOnNoChanges = true } = options;
  if (changedFields.size === 0) {
    if (notifyOnNoChanges) {
      setNotification({ type: 'error', message: 'No changes detected. Nothing to save.' });
    }
    return null;
  }

  const validation = validateData(jsonData);
  if (!validation.valid) {
    setNotification({ type: 'error', message: validation.message });
    return null;
  }

  localStorage.setItem('savedJsonData', JSON.stringify(jsonData));
  const cleanedData = cleanData(jsonData);
  const extractedData = extractCoursesMin(cleanedData);

  try {
    const response = await authFetch(`${config.SMA_SCHEDULER_SERVICE_BASE_URL}/config-post`, {
      method: 'POST',
      body: JSON.stringify(extractedData),
    });

    if (response.ok) {
      const result = await response.json();
      setNotification({ type: 'success', message: 'Data saved successfully! New backup: ' + result.filename });
      setCurrentBackupVersion(result.version);
      setChangedFields(new Set());
      fetchBackupFiles();
      return result.version || null;
    } else {
      const errorData = await response.json();
      setNotification({ type: 'error', message: `Error saving backup: ${errorData.detail || 'Unauthorized'}` });
      return null;
    }
  } catch (error) {
    setNotification({ type: 'error', message: 'Error: ' + error.message });
    return null;
  }
};

// Function to handle load backup
export const handleLoadBackup = async (filename, setJsonData, setChangedFields, setIsLoadedFromBackup, setCurrentBackupVersion, setNotification, currentBackupVersion) => {
  try {
    const response = await authFetch(`${config.SMA_SCHEDULER_SERVICE_BASE_URL}/config-get/${filename}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();

    // Verification check for USE_STATIC_COURSES
    if (!('USE_STATIC_COURSES' in data)) {
      try {
        const configResponse = await authFetch(`${config.SMA_SCHEDULER_SERVICE_BASE_URL}/config-current-get`);
        if (configResponse.ok) {
          const currentConfig = await configResponse.json();
          data.USE_STATIC_COURSES = currentConfig.USE_STATIC_COURSES || "false";
        } else {
          data.USE_STATIC_COURSES = "false";
        }
      } catch (configError) {
        data.USE_STATIC_COURSES = "false";
      }
    }

    setJsonData(data);
    setChangedFields(new Set());
    setIsLoadedFromBackup(true);
    localStorage.setItem('savedJsonData', JSON.stringify(data));

    const versionMatch = filename.match(/SMA-scheduler-config-v(\d+)-/);
    const versionLabel = versionMatch ? `v${versionMatch[1]}` : 'unknown';
    
    setCurrentBackupVersion(versionMatch ? `v${versionMatch[1]}` : null);
    setNotification({ type: 'success', message: `Backup config ${versionLabel} loaded successfully! `});
  } catch (error) {
    setNotification({ type: 'error', message: 'Error loading backup: ' + error.message });
  }
}


// Hook for overlay logic
export const useOverlay = (isActive) => {
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    if (isActive) {
      setShowOverlay(true);
    } else {
      setShowOverlay(false);
    }
  }, [isActive]);

  return showOverlay;
};

// Function for running scheduler
export const runScheduler = async (
  jsonData,
  setNotification,
  setOutputFiles,
  setJsonData,
  setChangedFields,
  setIsLoadedFromBackup,
  currentBackupVersion,
  setIsSchedulerRunning,
  fetchBackupFiles,
  setCurrentBackupVersion
) => {
  console.info("runScheduler called with currentBackupVersion:", currentBackupVersion);

  const validation = validateData(jsonData);
  if (!validation.valid) {
    setNotification({ type: 'error', message: validation.message });
    return;
  }

  const months = getMonthsFromDates(jsonData.startDate, jsonData.endDate);
  if (months.length === 0) {
    setNotification({ type: 'error', message: 'Invalid date range: start date is after end date.' });
    return;
  }

  // Detect whether the current configuration differs from the last saved one
  const cleanedCurrent = cleanData(jsonData);
  let savedRaw = null;
  try {
    savedRaw = localStorage.getItem('savedJsonData');
  } catch (e) {
    savedRaw = null;
  }
  let savedClean = null;
  if (savedRaw) {
    try {
      savedClean = cleanData(JSON.parse(savedRaw));
    } catch (e) {
      savedClean = null;
    }
  }

  // If different, POST a new config to create a new backup before running
  let versionForRun = currentBackupVersion;
  const currentString = JSON.stringify(cleanedCurrent || {});
  const savedString = JSON.stringify(savedClean || {});
  if (currentString !== savedString) {
    // Changes detected — generate a new configuration backup
    const extractedData = extractCoursesMin(cleanedCurrent);
    try {
      const resp = await authFetch(`${config.SMA_SCHEDULER_SERVICE_BASE_URL}/config-post`, {
        method: 'POST',
        body: JSON.stringify(extractedData),
      });
      if (!resp.ok) {
        const errText = await resp.text();
        setNotification({ type: 'error', message: `Failed to save config before run: ${errText}` });
        return;
      }
      const result = await resp.json();
      // Persist saved copy locally and update UI state
      try { localStorage.setItem('savedJsonData', JSON.stringify(cleanedCurrent)); } catch (e) {}
      setChangedFields(new Set());
      if (setCurrentBackupVersion && result && result.version) {
        setCurrentBackupVersion(result.version);
        versionForRun = result.version;
      }
      if (fetchBackupFiles) {
        try { await fetchBackupFiles(); } catch (e) {}
      }
      setNotification({ type: 'success', message: 'Configuration auto-saved before scheduler run.' });
    } catch (err) {
      setNotification({ type: 'error', message: 'Error saving configuration before run: ' + err.message });
      return;
    }
  }

  setIsSchedulerRunning(true);
  try {
    await handleMonthConfirm(jsonData, months, setNotification, setOutputFiles, setJsonData, setChangedFields, setIsLoadedFromBackup, versionForRun);
  } finally {
    setIsSchedulerRunning(false);
  }
};

const handleMonthConfirm = async (jsonData, months, setNotification, setOutputFiles, setJsonData, setChangedFields, setIsLoadedFromBackup, currentBackupVersion) => {
  const cleanedData = cleanData(jsonData);
  const extractedData = extractCoursesMin(cleanedData);

  console.info("currentBackupVersion in handleMonthConfirm:", currentBackupVersion);

  const refreshOutputFiles = async () => {
    try {
      const outputResponse = await authFetch(`${config.SMA_SCHEDULER_SERVICE_BASE_URL}/schedule-files-get`);
      if (outputResponse.ok) {
        const data = await outputResponse.json();
        setOutputFiles(data.files);
      } else {
        console.error('Failed to fetch Schedule files');
      }
    } catch (err) {
      console.error('Error fetching Schedule files:', err);
    }
  };

  try {
    const monthsParam = months.join(',');
    let url = `${config.SMA_SCHEDULER_SERVICE_BASE_URL}/run-scheduler?months=` + encodeURIComponent(monthsParam);
    if (currentBackupVersion) {
      url += '&version=' + encodeURIComponent(currentBackupVersion);
      console.info("Adding version to URL:", currentBackupVersion);
    } else {
      console.info("No currentBackupVersion available");
    }

    console.info("Final URL:", url);

    const response = await authFetch(url, {
      method: 'POST',
      body: JSON.stringify(extractedData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = errorText;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.detail || errorData.message || errorText;
      } catch (e) {
        // Not JSON, use text as is
      }
      const REPORT_DRIVE_TOKEN = '|REPORT_DRIVE_LINK:';
      const REPORT_PATH_TOKEN = '|REPORT_PATH:';
      if (errorMessage && errorMessage.includes(REPORT_DRIVE_TOKEN)) {
        const [messageText, driveLinkRaw] = errorMessage.split(REPORT_DRIVE_TOKEN);
        const driveLink = driveLinkRaw?.trim();
        setNotification({
          type: 'error',
          message: (
            <span>
              {messageText}
              {driveLink && (
                <>
                  <br />
                  <a
                    href={driveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#007bff', textDecoration: 'underline', marginLeft: '5px' }}
                  >
                    View failure report on Google Drive
                  </a>
                </>
              )}
            </span>
          ),
        });
      } else if (errorMessage && errorMessage.includes(REPORT_PATH_TOKEN)) {
        const [messageText, reportPath] = errorMessage.split(REPORT_PATH_TOKEN);
        const reportFilename = reportPath.split('/').pop();
        const downloadLink = `${config.SMA_SCHEDULER_SERVICE_BASE_URL}/report-get/${reportFilename}`;
        const messageNodes = [];
        if (messageText.includes('report.txt')) {
          const parts = messageText.split('report.txt');
          parts.forEach((part, index) => {
            if (part) {
              messageNodes.push(part);
            }
            if (index < parts.length - 1) {
              messageNodes.push(
                <a
                  key={`report-link-${index}`}
                  href={downloadLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#007bff', textDecoration: 'underline', marginLeft: '5px' }}
                >
                  report.txt
                </a>
              );
            }
          });
        } else {
          messageNodes.push(messageText);
          messageNodes.push(' ');
          messageNodes.push(
            <a
              href={downloadLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#007bff', textDecoration: 'underline', marginLeft: '5px' }}
            >
              report.txt
            </a>
          );
        }
        setNotification({
          type: 'error',
          message: <span>{messageNodes}</span>,
        });
      } else {
        setNotification({ type: 'error', message: errorMessage });
      }
      await refreshOutputFiles();
      return;
    }

    const result = await response.json();
    
    // Check if the scheduler failed
    if (result.status === 'failed') {
      // Handle failure case - show the original error message with a link to the Google Drive report
      const failureReportFilename = result.failure_report_filename;
      let messageContent = result.message || 'Scheduling failed';
      
      // If we have a Google Drive file ID, add a clickable link
      if (failureReportFilename) {
        // Construct Google Drive link using the file ID
        const driveLink = `https://drive.google.com/file/d/${failureReportFilename}/view`;
        
        // Add a link at the end with the text "View failure report on Google Drive"
        messageContent = (
          <span>
            {result.message}
            <br />
            <a
              href={driveLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#007bff', textDecoration: 'underline', marginLeft: '5px' }}
            >
              View failure report on Google Drive
            </a>
          </span>
        );
      }
      
      setNotification({ 
        type: 'error', 
        message: messageContent 
      });
    } else {
      // Handle success case
      const reportFilename = result.report_filename || 'report file';
      setNotification({ 
        type: 'success', 
        message: `Scheduler run successfully! Created schedule files: ${result.json_filename} and ${result.xlsx_filename} and ${reportFilename}` 
      });
    }
    await refreshOutputFiles();

    const updatedDataResponse = await authFetch(`${config.SMA_SCHEDULER_SERVICE_BASE_URL}/config-current-get`);
    if (updatedDataResponse.ok) {
      const updatedData = await updatedDataResponse.json();
      setJsonData(updatedData);
      setIsLoadedFromBackup(false);
    } else {
      console.error('Failed to fetch updated JSON data');
    }

  } catch (error) {
    setNotification({ type: 'error', message: 'Error: ' + error.message });
  }
};
